import { usePOSSummary } from "@faire/deprecated-retailer-visitor-shared/features/PointOfSale/hooks/usePOSSummary";
import { useInsiderNotificationType } from "@faire/deprecated-retailer-visitor-shared/hooks/accountMenu/useInsiderNotificationType";
import { useInsiderState } from "@faire/deprecated-retailer-visitor-shared/hooks/insider/useInsiderState";
import { useInsider } from "@faire/deprecated-retailer-visitor-shared/hooks/retailer/useInsider";
import { useRetailerFinancial } from "@faire/deprecated-retailer-visitor-shared/hooks/retailer/useRetailerFinancial";
import { useBrandDiscounts } from "@faire/deprecated-retailer-visitor-shared/stores/domain/useBrandDiscounts";
import { useUserStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/useUserStore";
import { cartHasValidBrandDiscounts } from "@faire/deprecated-retailer-visitor-shared/views/settings/cartHasValidBrandDiscounts";
import { SIDE_BAR_HEADINGS } from "@faire/deprecated-retailer-visitor-shared/views/settings/profile/consts";
import { trackRetailerStoreManagementAccountSettingsStoresLinkClick } from "@faire/web-api--source/events/retailerStoreManagement/click/accountSettingsStoresLink";
import { trackRetailerStoreManagementAccountSettingsStoresLinkView } from "@faire/web-api--source/events/retailerStoreManagement/view/accountSettingsStoresLink";
import { trackUpmarketMyTeamsLinkAccountSettingsClick } from "@faire/web-api--source/events/upmarket/click/myTeamsLinkAccountSettings";
import { trackUpmarketMyTeamsLinkAccountSettingsView } from "@faire/web-api--source/events/upmarket/view/myTeamsLinkAccountSettings";
import availableCredit from "@faire/web-api--source/routes/www/retailer/settings/available-credit";
import emailPreferences from "@faire/web-api--source/routes/www/retailer/settings/email-preferences";
import insiderMembership from "@faire/web-api--source/routes/www/retailer/settings/insider-membership";
import myTeam from "@faire/web-api--source/routes/www/retailer/settings/my-team";
import paymentMethods from "@faire/web-api--source/routes/www/retailer/settings/payment-methods";
import posManager from "@faire/web-api--source/routes/www/retailer/settings/pos-manager";
import profileSettings from "@faire/web-api--source/routes/www/retailer/settings/profile";
import securitySettings from "@faire/web-api--source/routes/www/retailer/settings/security";
import retailerSettingsShippingPreferences from "@faire/web-api--source/routes/www/retailer/settings/shipping-preferences";
import stores from "@faire/web-api--source/routes/www/retailer/settings/stores";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useMemo } from "react";

export enum NotificationIconType {
  NOTIFICATION = "notification",
  CRITICAL = "critical",
}

/** Represents a single item in the side bar menu. */
export interface IMenuItem {
  /** Unique value used to identify the menu item. */
  value: string;

  /** Display text of the menu item. */
  label: string;

  /** Path or URL the menu item links to. */
  path: string;

  /** Indicates the type of notification icon to display (if any).
   * "notification" represents a general notification,
   * while "critical" indicates an urgent or error state. */
  notificationIconType?: NotificationIconType;

  section?: SIDE_BAR_HEADINGS;

  /** Optional click handler function that will be triggered when the menu item is clicked. */
  handleClick?: () => void;

  /** Optional view handler function that will be triggered when the menu item is visible. */
  handleOnVisible?: () => void;
}

/** Provides the list of menu items for the settings sidebar. */
export const useMenuItems = (): IMenuItem[] => {
  const { hasLinkedPOS, name } = usePOSSummary();
  const { strictLocalize } = useStrictLocalization();
  const { accountType } = useUserStore();
  const { faireCredit } = useRetailerFinancial();
  const { isEligibleForInsider } = useInsider();

  const { isInsiderPaused, isInsiderActive } = useInsiderState();

  const insiderNotificationType = useInsiderNotificationType();

  const { brandDiscounts } = useBrandDiscounts();

  return useMemo(() => {
    const hasCredits =
      faireCredit > 0 || cartHasValidBrandDiscounts(brandDiscounts);

    const menuItems: IMenuItem[] = [
      {
        value: "profile",
        label: strictLocalize({
          id: "LjM//z",
          defaultMessage: "Profile",
          description: {
            text: "Item in the account settings menu",
            img: "https://cdn.faire.com/fastly/d02184adc03b8f13c246fc83ca14e365d8a9bedc45f759f9a288fcc1671c1e53.png",
          },
        }),
        path: profileSettings(),
        section: SIDE_BAR_HEADINGS.SETTINGS,
      },
      {
        value: "stores-management",
        label: strictLocalize({
          id: "8D3MhI",
          defaultMessage: "Stores",
          description: {
            text: "Item in the account settings menu leading to the stores management page, where businesses can manage their stores",
            img: "https://cdn.faire.com/fastly/827d33fc1fd6504865c9345031638fba78d591d306d54bb9bd3a2e94cd1b10da.png",
          },
        }),
        handleClick: () =>
          trackRetailerStoreManagementAccountSettingsStoresLinkClick(),
        path: stores(),
        handleOnVisible: () =>
          trackRetailerStoreManagementAccountSettingsStoresLinkView(),
        section: SIDE_BAR_HEADINGS.DEFAULT,
      },
      {
        value: "my-team",
        label: strictLocalize({
          id: "mp1lSN",
          defaultMessage: "Team",
          description: {
            text: "Item in the account settings menu",
            img: "https://cdn.faire.com/fastly/a2612691fbc4e434eedb9132d936e4ae189d67a5bb3875ac0a1624c575496c40.png",
          },
        }),
        path: myTeam(),
        handleClick: () =>
          trackUpmarketMyTeamsLinkAccountSettingsClick(accountType),
        handleOnVisible: () =>
          trackUpmarketMyTeamsLinkAccountSettingsView(accountType),
        section: SIDE_BAR_HEADINGS.DEFAULT,
      },
    ];

    if (hasCredits) {
      menuItems.push({
        value: "available-credit",
        label: strictLocalize({
          id: "6/Xy8O",
          defaultMessage: "Available credit",
          description: {
            text: "Item in the account settings menu",
            img: "https://cdn.faire.com/fastly/616e4cc4efb860454ae15d608183c30ff506c6015e8613332c7fbfcfccd91a35.png",
          },
        }),
        path: availableCredit(),
        section: SIDE_BAR_HEADINGS.DEFAULT,
      });
    }

    menuItems.push({
      value: "payment_methods",
      label: strictLocalize({
        id: "KoL5bc",
        defaultMessage: "Payment methods",
        description: {
          text: "Item in the account settings menu",
          img: "https://cdn.faire.com/fastly/616e4cc4efb860454ae15d608183c30ff506c6015e8613332c7fbfcfccd91a35.png",
        },
      }),
      path: paymentMethods(),
      section: SIDE_BAR_HEADINGS.DEFAULT,
    });

    menuItems.push({
      value: "shipping_preferences",
      label: strictLocalize({
        id: "0VHTck",
        defaultMessage: "Shipping",
        description: {
          text: "Item in the account settings menu",
          img: "https://cdn.faire.com/fastly/76e929ff7ca337e6912ecdb38ce19132d8c25857970061e734513b3d2f011cd4.png",
        },
      }),
      path: retailerSettingsShippingPreferences(),
    });

    if (hasLinkedPOS) {
      menuItems.push({
        value: "pos-manager",
        label: strictLocalize(
          {
            id: "wGHX5j",
            defaultMessage: "{posCompanyName} integration",
            description: {
              text: "Item in the account settings menu",
              img: "https://cdn.faire.com/fastly/616e4cc4efb860454ae15d608183c30ff506c6015e8613332c7fbfcfccd91a35.png",
            },
          },
          { posCompanyName: name }
        ),
        path: posManager(),
        section: SIDE_BAR_HEADINGS.DEFAULT,
      });
    }

    const insiderLabel = strictLocalize({
      id: "Eh9Kki",
      defaultMessage: "Insider",
      description: {
        text: "Item in the account settings menu",
        img: "https://cdn.faire.com/fastly/d02184adc03b8f13c246fc83ca14e365d8a9bedc45f759f9a288fcc1671c1e53.png",
      },
    });

    if (isInsiderActive || isInsiderPaused || isEligibleForInsider) {
      menuItems.push({
        value: "insider_membership",
        label: insiderLabel,
        path: insiderMembership(),
        notificationIconType:
          isInsiderActive || isInsiderPaused
            ? insiderNotificationType
            : undefined,
        section: SIDE_BAR_HEADINGS.DEFAULT,
      });
    }

    menuItems.push({
      value: "email_preferences",
      label: strictLocalize({
        id: "AtDx5j",
        defaultMessage: "Email preferences",
        description: {
          text: "Item in the account settings menu",
          img: "https://cdn.faire.com/fastly/616e4cc4efb860454ae15d608183c30ff506c6015e8613332c7fbfcfccd91a35.png",
        },
      }),
      path: emailPreferences(),
      section: SIDE_BAR_HEADINGS.SETTINGS,
    });

    menuItems.push({
      value: "security",
      label: strictLocalize({
        id: "1Jt3RS",
        defaultMessage: "Security & privacy",
        description: {
          text: "Item in the account settings menu",
          img: "https://cdn.faire.com/fastly/616e4cc4efb860454ae15d608183c30ff506c6015e8613332c7fbfcfccd91a35.png",
        },
      }),
      path: securitySettings(),
      section: SIDE_BAR_HEADINGS.SETTINGS,
    });

    return menuItems;
  }, [
    faireCredit,
    brandDiscounts,
    strictLocalize,
    hasLinkedPOS,
    isInsiderActive,
    isInsiderPaused,
    isEligibleForInsider,
    name,
    insiderNotificationType,
    accountType,
  ]);
};
